import dashboardStore from '@stores/dashboard'
import { message, Modal } from 'antd'

import {
  AssistantNavigationTarget,
  isStandaloneAssistant,
  openInNewAppTab,
  sendToApp,
} from './assistantBridge'

/** `route?query` — the query is URL-encoded; empty values are kept (the list screens read them). */
export const buildAssistantPath = (
  route: string,
  query?: Record<string, string> | null
) => {
  const qs = new URLSearchParams(query ?? {}).toString()
  return qs ? `${route}?${qs}` : route
}

/**
 * Standalone page: hand the link to the main app and say where it went. When no Kawaconn tab is
 * open, ask before opening one — and link the Assistant to it for the next links.
 */
const openInApp = async (target: AssistantNavigationTarget) => {
  if ((await sendToApp(target)) !== 'none') {
    message.info('Opened in your Kawaconn tab')
    return
  }

  Modal.confirm({
    title: 'Your Kawaconn tab is closed',
    content:
      'Open this link in a new tab? The Assistant will open its next links in that tab too.',
    okText: 'Open in new tab',
    cancelText: 'Cancel',
    onOk: () => {
      if (!openInNewAppTab(target)) {
        message.warning(
          'Your browser blocked the new tab. Allow pop-ups for Kawaconn and try again.'
        )
      }
    },
  })
}

/**
 * Navigates the main app (in this tab). List screens read their filters from the URL only when
 * they mount, so a filtered link to a tab that is already open closes it first and reopens it on
 * the next tick — the same thing the dashboard's own statistic links do.
 */
export const navigateHere = (target: AssistantNavigationTarget) => {
  if (target.kind === 'record') {
    dashboardStore.open(target.target, target.options)
    return
  }
  const { route, query, title } = target
  const path = buildAssistantPath(route, query)
  const options = title ? { title } : undefined
  if (!query || Object.keys(query).length === 0) {
    dashboardStore.open(path, options)
    return
  }
  dashboardStore.close(route)
  setTimeout(() => dashboardStore.open(path, options))
}

/**
 * Opens a server-built SPA link (handoff, briefing item) in the main app — directly in the
 * drawer, or through the bridge from the standalone Assistant page.
 */
export const openAssistantRoute = (
  route: string,
  query?: Record<string, string> | null,
  title?: string
) => {
  const target: AssistantNavigationTarget = {
    kind: 'route',
    route,
    query,
    title,
  }
  if (isStandaloneAssistant()) {
    openInApp(target)
  } else {
    navigateHere(target)
  }
}

/** Opens a record's dashboard tab (route config from ROUTE_PATH) in the main app. */
export const openAssistantRecord = (target: any, options?: any) => {
  const navigation: AssistantNavigationTarget = {
    kind: 'record',
    target,
    options,
  }
  if (isStandaloneAssistant()) {
    openInApp(navigation)
  } else {
    navigateHere(navigation)
  }
}
